import type { FrameName } from "@/game/entities/config";
import useSegmentManager from "@/hooks/useSegmentManager";
import { SocialMediaType } from "@/libs/AppCommunicatior";
import type { LeaderboardResult } from "@/types";
import { FilterItems } from "@/types/upgrades";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { watch, type ComputedRef } from "vue";

export enum SuperClickerEnum {
	TEN = 10,
	TWO = 2,
	THREE = 3,
	FIVE = 5,
}

export enum RecordedActivity {
	OFFLINE_INCOME_CLOSED = "offline_income_closed",
	DAILY_REWARD_CLOSED = "daily_reward_closed",
}

/**
 * Deep link data received from Flutter, awaiting processing.
 *
 * When Flutter calls `sendDeepLinkData(...)`, the string value is stored here
 * and processed once the game finishes loading. Set to `null` after processing.
 *
 * @see {@link ../../utils/deepLinkHandler} for processing logic.
 */
export type DeepLinkData = string | null;

type CoreStoreState = {
	isLoadingDone: boolean;
	isPhaserReady: boolean;
	isCameraActive: boolean;
	superClickerActive: boolean;
	superClickerType: SuperClickerEnum;
	superClickerInitialized: boolean;
	poiJustRevealed: boolean;
	upgradeFilterValue: FilterItems;
	firstClick: boolean;
	lastClickTimestamp: number | null;
	lastCameraChangeTimestamp: number | null;
	isInactivityPopupVisible: boolean;
	lastRecordedActivity: RecordedActivity | null;
	leaderboardLoadTimestamp: number | null;
	leaderboardData: LeaderboardResult | null;
	incomeBoostActive: boolean;
	incomeBoostAdActive: boolean;
	incomeBoostPopupVisible: boolean;
	infoNotifications: number;
	linkingAccountSocialMediaType: SocialMediaType;
	showUseSuperClickerTooltip: boolean;
	searchToolActiveFrameName: FrameName[] | null;
	isSettlementWindowCollapsed: boolean;
	isMaintenanceMode: boolean;
	/**
	 * Deep link data received from Flutter, awaiting processing.
	 * Set by `flutterWindowMethods.ts` when `sendDeepLinkData` is called.
	 * Cleared after the deep link is processed in App.vue.
	 */
	pendingDeepLink: DeepLinkData;
	/**
	 * Referral data received from Flutter via `sendReferralData`.
	 * Set by `flutterWindowMethods.ts` when Flutter sends referral info.
	 */
	referralData: Record<string, unknown> | null;
};

type CoreStore = {
	isLoadingDone: ComputedRef<boolean>;
	isPhaserReady: ComputedRef<boolean>;
	isCameraActive: ComputedRef<boolean>;
	superClickerActive: ComputedRef<boolean>;
	superClickerType: ComputedRef<SuperClickerEnum>;
	superClickerInitialized: ComputedRef<boolean>;
	poiJustRevealed: ComputedRef<boolean>;
	upgradeFilterValue: ComputedRef<FilterItems>;
	firstClick: ComputedRef<boolean>;
	lastClickTimestamp: ComputedRef<number | null>;
	lastCameraChangeTimestamp: ComputedRef<number | null>;
	isInactivityPopupVisible: ComputedRef<boolean>;
	lastRecordedActivity: ComputedRef<RecordedActivity | null>;
	leaderboardLoadTimestamp: ComputedRef<number | null>;
	leaderboardData: ComputedRef<LeaderboardResult | null>;
	incomeBoostActive: ComputedRef<boolean>;
	incomeBoostAdActive: ComputedRef<boolean>;
	incomeBoostPopupVisible: ComputedRef<boolean>;
	infoNotifications: ComputedRef<number>;
	linkingAccountSocialMediaType: ComputedRef<SocialMediaType>;
	showUseSuperClickerTooltip: ComputedRef<boolean>;
	searchToolActiveFrameName: ComputedRef<FrameName[] | null>;
	isSettlementWindowCollapsed: ComputedRef<boolean>;
	isMaintenanceMode: ComputedRef<boolean>;
	/** Deep link data awaiting processing. */
	pendingDeepLink: ComputedRef<DeepLinkData>;
	/** Referral data received from Flutter. */
	referralData: ComputedRef<Record<string, unknown> | null>;
	/** Whether the welcome-back reward has already been claimed. */
	setSuperClicker(active: boolean): void;
	setSuperClickerType(type: SuperClickerEnum): void;
	setLoadingDone(value: boolean): void;
	setPhaserReady(value: boolean): void;
	setCameraActive(active: boolean): void;
	setPoiJustRevealed(value: boolean): void;
	setUpgradeFilterValue(value: FilterItems): void;
	setFirstClick(value: boolean): void;
	setLastClickTimestamp(timestamp: number | null): void;
	setLastCameraChangeTimestamp(timestamp: number | null): void;
	setIsInactivityPopupVisible(visible: boolean): void;
	setLastRecordedActivity(activity: RecordedActivity | null): void;
	setLeaderboardData(data: LeaderboardResult | null): void;
	setIncomeBoostActive(active: boolean): void;
	setIncomeBoostAdActive(active: boolean): void;
	setIncomeBoostPopupVisible(visible: boolean): void;
	setInfoNotifications(count: number): void;
	setLinkingAccountSocialMediaType(mediaType: SocialMediaType): void;
	setShowUseSuperClickerTooltip(value: boolean): void;
	setSearchToolActiveFrameName(frameName: FrameName[] | null): void;
	setIsSettlementWindowCollapsed(value: boolean): void;
	setIsMaintenanceMode(value: boolean): void;
	/** Sets the pending deep link data for processing. */
	setPendingDeepLink(data: DeepLinkData): void;
	/** Sets the referral data received from Flutter. */
	setReferralData(data: Record<string, unknown> | null): void;
	/** Marks the welcome-back reward as claimed. */
	setSuperClickerInitialized(initialized: boolean): void;
};

const STORE_NAME = "runtime";

export default defineStore(STORE_NAME, (): CoreStore => {
	// Immutable state can never be changed directly,
	// but can be replaced with a new version of the state containing the desired changes.
	const state = createImmutableState<CoreStoreState>({
		isLoadingDone: false,
		isPhaserReady: false,
		isCameraActive: false,
		superClickerActive: false,
		superClickerType: SuperClickerEnum.TEN,
		superClickerInitialized: false,
		poiJustRevealed: false,
		upgradeFilterValue: FilterItems.NEWEST,
		firstClick: false,
		lastClickTimestamp: null,
		lastCameraChangeTimestamp: null,
		isInactivityPopupVisible: false,
		lastRecordedActivity: null,
		leaderboardLoadTimestamp: null,
		leaderboardData: null,
		incomeBoostActive: false,
		incomeBoostAdActive: false,
		incomeBoostPopupVisible: false,
		infoNotifications: 0,
		linkingAccountSocialMediaType: SocialMediaType.NONE,
		showUseSuperClickerTooltip: false,
		searchToolActiveFrameName: null,
		isSettlementWindowCollapsed: false,
		isMaintenanceMode: false,
		// Deep link data from Flutter, awaiting processing (null = none pending)
		pendingDeepLink: null as DeepLinkData,
		// Referral data from Flutter (null = none pending)
		referralData: null,
		// Whether the welcome-back reward has been claimed (TODO: persist to BE)
	});

	const isLoadingDone = getImmutableStateProperty(
		state,
		"isLoadingDone",
		false,
	);

	const isPhaserReady = getImmutableStateProperty(
		state,
		"isPhaserReady",
		false,
	);

	const isCameraActive = getImmutableStateProperty(
		state,
		"isCameraActive",
		false,
	);

	const superClickerActive = getImmutableStateProperty(
		state,
		"superClickerActive",
		false,
	);

	const superClickerType = getImmutableStateProperty(
		state,
		"superClickerType",
		SuperClickerEnum.TEN,
	);

	const superClickerInitialized = getImmutableStateProperty(
		state,
		"superClickerInitialized",
		false,
	);

	const poiJustRevealed = getImmutableStateProperty(
		state,
		"poiJustRevealed",
		false,
	);

	const upgradeFilterValue = getImmutableStateProperty(
		state,
		"upgradeFilterValue",
		FilterItems.NEWEST,
	);

	const firstClick = getImmutableStateProperty(state, "firstClick", false);
	const lastClickTimestamp = getImmutableStateProperty(
		state,
		"lastClickTimestamp",
		null,
	);

	const isInactivityPopupVisible = getImmutableStateProperty(
		state,
		"isInactivityPopupVisible",
		false,
	);

	const lastRecordedActivity = getImmutableStateProperty(
		state,
		"lastRecordedActivity",
		null,
	);

	const leaderboardLoadTimestamp = getImmutableStateProperty(
		state,
		"leaderboardLoadTimestamp",
		null,
	);

	const leaderboardData = getImmutableStateProperty(
		state,
		"leaderboardData",
		null,
	);

	const lastCameraChangeTimestamp = getImmutableStateProperty(
		state,
		"lastCameraChangeTimestamp",
		null,
	);

	const incomeBoostActive = getImmutableStateProperty(
		state,
		"incomeBoostActive",
		false,
	);

	const incomeBoostAdActive = getImmutableStateProperty(
		state,
		"incomeBoostAdActive",
		false,
	);

	const incomeBoostPopupVisible = getImmutableStateProperty(
		state,
		"incomeBoostPopupVisible",
		false,
	);

	const infoNotifications = getImmutableStateProperty(
		state,
		"infoNotifications",
		0,
	);

	const linkingAccountSocialMediaType = getImmutableStateProperty(
		state,
		"linkingAccountSocialMediaType",
		SocialMediaType.NONE,
	);

	const showUseSuperClickerTooltip = getImmutableStateProperty(
		state,
		"showUseSuperClickerTooltip",
		false,
	);

	const searchToolActiveFrameName = getImmutableStateProperty(
		state,
		"searchToolActiveFrameName",
		null,
	);

	const isSettlementWindowCollapsed = getImmutableStateProperty(
		state,
		"isSettlementWindowCollapsed",
		false,
	);

	const isMaintenanceMode = getImmutableStateProperty(
		state,
		"isMaintenanceMode",
		false,
	);

	/** Computed ref for the pending deep link data from Flutter. */
	const pendingDeepLink = getImmutableStateProperty(
		state,
		"pendingDeepLink",
		null,
	);

	/** Computed ref for referral data received from Flutter. */
	const referralData = getImmutableStateProperty(state, "referralData", null);

	const setLoadingDone = (value: boolean): void =>
		setImmutableStateProperty(state, "isLoadingDone", value);

	const setPhaserReady = (value: boolean): void =>
		setImmutableStateProperty(state, "isPhaserReady", value);

	const setCameraActive = (value: boolean): void =>
		setImmutableStateProperty(state, "isCameraActive", value);

	const setSuperClicker = (active: boolean): void => {
		setImmutableStateProperty(state, "superClickerActive", active);
	};

	const setSuperClickerType = (type: SuperClickerEnum): void => {
		setImmutableStateProperty(state, "superClickerType", type);
	};

	const setSuperClickerInitialized = (initialized: boolean): void => {
		setImmutableStateProperty(
			state,
			"superClickerInitialized",
			initialized,
		);
	};

	const setPoiJustRevealed = (value: boolean): void =>
		setImmutableStateProperty(state, "poiJustRevealed", value);

	const setUpgradeFilterValue = (value: FilterItems): void =>
		setImmutableStateProperty(state, "upgradeFilterValue", value);

	const setFirstClick = (value: boolean): void =>
		setImmutableStateProperty(state, "firstClick", value);

	const setLastClickTimestamp = (timestamp: number | null): void =>
		setImmutableStateProperty(state, "lastClickTimestamp", timestamp);

	const setLastCameraChangeTimestamp = (timestamp: number | null): void =>
		setImmutableStateProperty(
			state,
			"lastCameraChangeTimestamp",
			timestamp,
		);

	const setIsInactivityPopupVisible = (visible: boolean): void =>
		setImmutableStateProperty(state, "isInactivityPopupVisible", visible);

	const setLastRecordedActivity = (activity: RecordedActivity | null): void =>
		setImmutableStateProperty(state, "lastRecordedActivity", activity);

	const setLeaderboardData = (data: LeaderboardResult | null): void => {
		setImmutableStateProperty(
			state,
			"leaderboardLoadTimestamp",
			Date.now(),
		);
		setImmutableStateProperty(state, "leaderboardData", data);
	};

	const setIncomeBoostActive = (active: boolean): void => {
		setImmutableStateProperty(state, "incomeBoostActive", active);
	};

	const setIncomeBoostAdActive = (active: boolean): void => {
		setImmutableStateProperty(state, "incomeBoostAdActive", active);
	};

	const setIncomeBoostPopupVisible = (visible: boolean): void => {
		setImmutableStateProperty(state, "incomeBoostPopupVisible", visible);
	};

	const setInfoNotifications = (count: number): void => {
		setImmutableStateProperty(state, "infoNotifications", count);
	};

	const setLinkingAccountSocialMediaType = (
		mediaType: SocialMediaType,
	): void => {
		setImmutableStateProperty(
			state,
			"linkingAccountSocialMediaType",
			mediaType,
		);
	};

	const setShowUseSuperClickerTooltip = (show: boolean): void => {
		setImmutableStateProperty(state, "showUseSuperClickerTooltip", show);
	};

	const setSearchToolActiveFrameName = (
		frameName: FrameName[] | null,
	): void => {
		setImmutableStateProperty(
			state,
			"searchToolActiveFrameName",
			frameName,
		);
	};

	const setIsMaintenanceMode = (value: boolean): void => {
		setImmutableStateProperty(state, "isMaintenanceMode", value);
	};

	/** Sets the pending deep link data for processing. */
	const setPendingDeepLink = (data: DeepLinkData): void => {
		setImmutableStateProperty(state, "pendingDeepLink", data);
	};

	/** Sets the referral data received from Flutter. */
	const setReferralData = (data: Record<string, unknown> | null): void => {
		setImmutableStateProperty(state, "referralData", data);
	};

	watch(
		(): boolean => incomeBoostActive.value,
		(): void => {
			useSegmentManager().run(true);
		},
	);

	const setIsSettlementWindowCollapsed = (value: boolean): void => {
		setImmutableStateProperty(state, "isSettlementWindowCollapsed", value);
	};

	return {
		isLoadingDone,
		isPhaserReady,
		isCameraActive,
		superClickerActive,
		superClickerType,
		poiJustRevealed,
		upgradeFilterValue,
		firstClick,
		lastClickTimestamp,
		lastCameraChangeTimestamp,
		isInactivityPopupVisible,
		lastRecordedActivity,
		leaderboardLoadTimestamp,
		leaderboardData,
		incomeBoostActive,
		incomeBoostAdActive,
		incomeBoostPopupVisible,
		infoNotifications,
		linkingAccountSocialMediaType,
		showUseSuperClickerTooltip,
		searchToolActiveFrameName,
		isSettlementWindowCollapsed,
		isMaintenanceMode,
		// Deep link related
		pendingDeepLink,
		referralData,
		superClickerInitialized,
		setSuperClicker,
		setSuperClickerType,
		setLoadingDone,
		setPhaserReady,
		setCameraActive,
		setPoiJustRevealed,
		setUpgradeFilterValue,
		setFirstClick,
		setLastClickTimestamp,
		setLastCameraChangeTimestamp,
		setIsInactivityPopupVisible,
		setLastRecordedActivity,
		setLeaderboardData,
		setIncomeBoostActive,
		setIncomeBoostAdActive,
		setIncomeBoostPopupVisible,
		setInfoNotifications,
		setLinkingAccountSocialMediaType,
		setShowUseSuperClickerTooltip,
		setSearchToolActiveFrameName,
		setIsSettlementWindowCollapsed,
		setIsMaintenanceMode,
		setPendingDeepLink,
		setReferralData,
		setSuperClickerInitialized,
	};
});
